import { useEffect, useCallback, useRef } from 'react'
import { useAppDispatch } from './useReduxHooks'
import { actions } from '../redux/reducers/notifications/slice'
import socketService from '../services/socketService'
import type { Notification as NotificationType } from '../redux/reducers/notifications/types'
import {
  formatNationalExamNotificationPushBody,
  formatNationalExamNotificationPushTitle,
  getNationalExamFromNotification,
} from '../helpers/notification-national-exam'
import {NATIONAL_EXAM_NOTIFICATION_TYPES} from '../helpers/nationalExamNotificationNavigation'
import {
  BILLING_UNPAID_NOTIFICATION_TYPE,
  formatBillingUnpaidPushBody,
  formatBillingUnpaidPushTitle,
} from '../helpers/notification-billing'

interface UseSocketNotificationsOptions {
  enabled?: boolean
  onNotification?: (notification: NotificationType) => void
  /** Check if a conversation is currently being viewed (for suppressing chat notifications) */
  isConversationViewed?: (conversationId: string) => boolean
}

/**
 * Socket.IO notifications hook. Integrates with Redux for notification:new and notification:read.
 * serverUrl must be the same as REST API base URL (see docs/FRONTEND-SOCKET-IO-CORS.md).
 * @param serverUrl - Same host as API (e.g. https://api.maarefati.com)
 * @param token - JWT (same as REST); when token changes, socket reconnects with new token
 * @param options - enabled, onNotification
 */
export function useSocketNotifications(
  serverUrl: string | undefined,
  token: string | null | undefined,
  options: UseSocketNotificationsOptions = {}
) {
  const dispatch = useAppDispatch()
  const { enabled = true, onNotification, isConversationViewed } = options
  const onNotificationRef = useRef(onNotification)
  const isConversationViewedRef = useRef(isConversationViewed)

  // Update refs when callbacks change
  useEffect(() => {
    onNotificationRef.current = onNotification
    isConversationViewedRef.current = isConversationViewed
  }, [onNotification, isConversationViewed])

  useEffect(() => {
    if (!enabled || !serverUrl || !token) {
      // Disconnect if conditions are not met
      socketService.disconnect()
      return
    }

    // Connect to Socket.IO
    socketService.connect(serverUrl, token, {
      onConnect: () => {},
      onDisconnect: (reason: string) => {
        if (reason === 'io server disconnect') {
          setTimeout(() => {
            if (serverUrl && token) {
              socketService.connect(serverUrl, token)
            }
          }, 1000)
        }
      },
      onError: () => {
        // Logged once in socketService (dev only) to avoid duplicate spam
      },
      onReconnect: () => {},
      onNotification: (notificationData: NotificationType) => {
        // Extract conversationId from URL for chat notifications (format: /chat/{conversationId})
        let conversationId: string | null = null
        if (notificationData.notificationType === 'NEW_CHAT_MESSAGE' && notificationData.url) {
          const match = notificationData.url.match(/\/chat\/([a-f0-9]{24})/i)
          conversationId = match ? match[1] : null
        }

        // Suppress browser notification if user is viewing this conversation
        const isViewingConversation = conversationId && isConversationViewedRef.current?.(conversationId)
        
        // Always add to Redux store (for notification list/badge)
        dispatch(actions.addRealTimeNotification(notificationData))

        // Call custom callback if provided
        if (onNotificationRef.current) {
          onNotificationRef.current(notificationData)
        }

        // Show browser notification only if not viewing the conversation
        if (!isViewingConversation && 'Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification(getNotificationMessage(notificationData), {
              body: getNotificationBody(notificationData),
              icon: '/custom_icons/maarefati-logo.png',
              tag: notificationData._id, // Prevent duplicate notifications
            })
          } catch (error) {
            console.warn('Failed to show browser notification:', error)
          }
        } else if (isViewingConversation && process.env.NODE_ENV === 'development') {
          console.debug('[notifications] Suppressed browser notification for viewed conversation', conversationId)
        }
      },
      onReadConfirmed: (data: { notificationId: string }) => {
        // Update notification read status in Redux
        dispatch(
          actions.updateNotificationReadStatus({
            notificationId: data.notificationId,
            read: true,
          })
        )
      },
    })

    // Cleanup on unmount
    return () => {
      socketService.disconnect()
    }
  }, [serverUrl, token, enabled, dispatch])

  const markAsRead = useCallback((notificationId: string) => {
    socketService.markNotificationAsRead(notificationId)
    // Update local state immediately (optimistic update)
    dispatch(
      actions.updateNotificationReadStatus({
        notificationId,
        read: true,
      })
    )
  }, [dispatch])

  const getConnectionStatus = useCallback(() => {
    return socketService.getConnectionStatus()
  }, [])

  return {
    markAsRead,
    getConnectionStatus,
  }
}

// Helper function to get notification message
function getNotificationMessage(notification: NotificationType): string {
  switch (notification.notificationType) {
    case 'ADD_EXERCICE':
      return 'Nouvel exercice'
    case 'DONE_EXERCICE':
      return 'Exercice terminé'
    case 'ADD_EXAM':
      return 'Nouvel examen programmé'
    case NATIONAL_EXAM_NOTIFICATION_TYPES.ADD:
      return formatNationalExamNotificationPushTitle(notification.notificationType)
    case NATIONAL_EXAM_NOTIFICATION_TYPES.CORRECTION_ADDED:
      return formatNationalExamNotificationPushTitle(notification.notificationType)
    case 'ADD_REMARQUE':
      return 'Nouvelle remarque'
    case 'NEW_CHAT_MESSAGE': {
      const sender = notification.sender as any
      const senderName = sender
        ? `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || 'Someone'
        : 'Admin'
      return `New message from ${senderName}`
    }
    case BILLING_UNPAID_NOTIFICATION_TYPE:
      return formatBillingUnpaidPushTitle()
    default:
      return 'Nouvelle notification'
  }
}

// Helper function to get notification body
function getNotificationBody(notification: NotificationType): string {
  switch (notification.notificationType) {
    case 'ADD_EXERCICE':
      return notification.exercice?.title || 'Un nouvel exercice a été ajouté'
    case 'DONE_EXERCICE':
      return notification.exercice?.title || 'Un exercice a été terminé'
    case 'ADD_EXAM':
      return 'Un nouvel examen a été programmé'
    case NATIONAL_EXAM_NOTIFICATION_TYPES.ADD:
    case NATIONAL_EXAM_NOTIFICATION_TYPES.CORRECTION_ADDED:
      return formatNationalExamNotificationPushBody(
        getNationalExamFromNotification(notification),
        notification.notificationType
      )
    case 'ADD_REMARQUE':
      return notification.remarque?.type?.name || 'Une nouvelle remarque a été ajoutée'
    case 'NEW_CHAT_MESSAGE':
      return 'You have a new message'
    case BILLING_UNPAID_NOTIFICATION_TYPE:
      return formatBillingUnpaidPushBody(notification)
    default:
      return 'Vous avez une nouvelle notification'
  }
}

export default useSocketNotifications
